import { useState, type FormEvent } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { AlertBanner, Button, FormField, Heading, Input, SelectField, Skeleton, tabularNumsClass } from "@lendcord/concord";
import { ProfileField } from "@/features/profile/components/ProfileField";
import { useGetApplicantProfileQuery, useUpdateApplicantProfileMutation } from "@/features/onboarding/api/onboarding.api";
import { isQueryApiError } from "@/app/store/baseApi";
import { formatWireDate, isoToWireDate, wireDateToIso } from "@/app/dateFormat";
import {
  AADHAAR_REGEX,
  ACCOUNT_NUMBER_REGEX,
  IFSC_REGEX,
  isAtLeast18,
  PAN_REGEX,
  PINCODE_REGEX,
} from "@/app/profile/validators";
import { EMAIL_REGEX, NAME_REGEX, PHONE_REGEX, toIndianE164 } from "@/auth/validators";
import type { EmploymentType, NomineeRelationship } from "@/app/profile/profile.types";

interface ApplicantProfileSectionProps {
  applicationId: string;
}

interface FormState {
  email: string;
  phone: string;
  branchId: string;
  pan: string;
  aadhaar: string;
  dob: string;
  fathersName: string;
  gender: string;
  addressLine1: string;
  addressLine2: string;
  addressCity: string;
  addressState: string;
  addressPincode: string;
  accountNumber: string;
  ifsc: string;
  accountHolderName: string;
  bankName: string;
  accountType: "SAVINGS" | "CURRENT" | "";
  nomineeName: string;
  nomineeContact: string;
  nomineeRelationship: NomineeRelationship | "";
  employmentType: EmploymentType | "";
  employerName: string;
  designation: string;
  monthlyIncome: string;
}

const EMPTY_FORM: FormState = {
  email: "",
  phone: "",
  branchId: "",
  pan: "",
  aadhaar: "",
  dob: "",
  fathersName: "",
  gender: "",
  addressLine1: "",
  addressLine2: "",
  addressCity: "",
  addressState: "",
  addressPincode: "",
  accountNumber: "",
  ifsc: "",
  accountHolderName: "",
  bankName: "",
  accountType: "",
  nomineeName: "",
  nomineeContact: "",
  nomineeRelationship: "",
  employmentType: "",
  employerName: "",
  designation: "",
  monthlyIncome: "",
};

const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0,
});

const GENDER_LABEL_IDS: Record<string, string> = {
  MALE: "lender.profile.genderMale",
  FEMALE: "lender.profile.genderFemale",
  OTHER: "lender.profile.genderOther",
};

const RELATIONSHIP_LABEL_IDS: Record<NomineeRelationship, string> = {
  SPOUSE: "lender.profile.relationshipSpouse",
  PARENT: "lender.profile.relationshipParent",
  CHILD: "lender.profile.relationshipChild",
  SIBLING: "lender.profile.relationshipSibling",
  OTHER: "lender.profile.relationshipOther",
};

const EMPLOYMENT_TYPE_LABEL_IDS: Record<EmploymentType, string> = {
  SALARIED: "lender.profile.employmentTypeSalaried",
  SELF_EMPLOYED: "lender.profile.employmentTypeSelfEmployed",
  BUSINESS: "lender.profile.employmentTypeBusiness",
  UNEMPLOYED: "lender.profile.employmentTypeUnemployed",
  RETIRED: "lender.profile.employmentTypeRetired",
  STUDENT: "lender.profile.employmentTypeStudent",
  OTHER: "lender.profile.employmentTypeOther",
};

const EMPLOYMENT_TYPE_OPTIONS: EmploymentType[] = [
  "SALARIED",
  "SELF_EMPLOYED",
  "BUSINESS",
  "UNEMPLOYED",
  "RETIRED",
  "STUDENT",
  "OTHER",
];

// The nominee's phone is stored E.164 (+91-only for now) — strip the prefix
// back off for editing, same as features/profile/components/KycDetails.tsx.
function stripIndianE164(value: string): string {
  return value.startsWith("+91") ? value.slice(3) : value;
}

// Reviewer-facing "2nd checkpoint" override (Form 1, before OTP verification)
// and the KYC-edit half of Form 2 (after VERIFIED) — same component, same
// GET/PUT .../applications/{id}/profile call for both, per lendcord-core's
// docs/flows/onboarding-review-flow.md, section 3. Unlike the self-serve
// Profile page (features/profile/), every section here saves through one
// bulk PUT rather than per-section endpoints, since that's the shape this
// review endpoint takes (it reuses ProfileService, not its individual
// controllers). Address is scoped to a single CURRENT entry, matching what
// the onboarding submission form itself ever collects — not the 3-type
// (CURRENT/PERMANENT/OFFICE) editor self-serve Profile offers.
export function ApplicantProfileSection({ applicationId }: ApplicantProfileSectionProps) {
  const intl = useIntl();
  const { data, isLoading, error } = useGetApplicantProfileQuery(applicationId);
  const [updateApplicantProfile, { isLoading: isSubmitting }] = useUpdateApplicantProfileMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [formError, setFormError] = useState<string | undefined>();

  // Bank fields become required together the moment any one of them is
  // touched; nominee name/relationship the same way — mirrors KycDetails'
  // pattern for a section that's otherwise entirely optional to edit here.
  const hasBankInput = Boolean(
    form.accountNumber || form.ifsc || form.accountHolderName || form.bankName || form.accountType
  );
  const hasNomineeInput = Boolean(form.nomineeName.trim() || form.nomineeRelationship || form.nomineeContact);

  function updateField<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function handleStartEdit() {
    if (!data) return;
    const { profile } = data;
    const address = profile.addresses.find((a) => a.type === "CURRENT") ?? profile.addresses[0];
    const bank = profile.bankDetails[0];
    setForm({
      email: profile.email,
      phone: stripIndianE164(profile.phone),
      branchId: data.branchSelection.defaultBranch?.branchId ?? "",
      // pan/aadhaar come back masked from GET — same as self-serve KycDetails,
      // start blank and only send if the reviewer actually enters a new one.
      pan: "",
      aadhaar: "",
      dob: profile.dob ? wireDateToIso(profile.dob) : "",
      fathersName: profile.fathersName ?? "",
      gender: profile.gender ?? "",
      addressLine1: address?.line1 ?? "",
      addressLine2: address?.line2 ?? "",
      addressCity: address?.city ?? "",
      addressState: address?.state ?? "",
      addressPincode: address?.pincode ?? "",
      // accountNumber is masked too and this is a full replace, not a merge —
      // same as self-serve BankAccountSection, must be re-entered.
      accountNumber: "",
      ifsc: bank?.ifsc ?? "",
      accountHolderName: bank?.accountHolderName ?? "",
      bankName: bank?.bankName ?? "",
      accountType: (bank?.accountType as FormState["accountType"]) ?? "",
      nomineeName: profile.nominee?.name ?? "",
      nomineeContact: profile.nominee?.phone ? stripIndianE164(profile.nominee.phone) : "",
      nomineeRelationship: profile.nominee?.relationship ?? "",
      employmentType: profile.employmentDetail?.employmentType ?? "",
      employerName: profile.employmentDetail?.employerName ?? "",
      designation: profile.employmentDetail?.designation ?? "",
      monthlyIncome:
        profile.employmentDetail?.monthlyIncome != null ? String(profile.employmentDetail.monthlyIncome) : "",
    });
    setErrors({});
    setFormError(undefined);
    setIsEditing(true);
  }

  function handleCancel() {
    setIsEditing(false);
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(undefined);

    const nextErrors: typeof errors = {};
    if (form.email && !EMAIL_REGEX.test(form.email)) {
      nextErrors.email = intl.formatMessage({ id: "lender.onboarding.review.emailFormatError" });
    }
    if (form.phone && !PHONE_REGEX.test(form.phone)) {
      nextErrors.phone = intl.formatMessage({ id: "lender.onboarding.review.phoneFormatError" });
    }
    if (form.pan && !PAN_REGEX.test(form.pan)) {
      nextErrors.pan = intl.formatMessage({ id: "lender.profile.panFormatError" });
    }
    if (form.aadhaar && !AADHAAR_REGEX.test(form.aadhaar)) {
      nextErrors.aadhaar = intl.formatMessage({ id: "lender.profile.aadhaarFormatError" });
    }
    if (form.dob && new Date(form.dob) >= new Date()) {
      nextErrors.dob = intl.formatMessage({ id: "lender.profile.dobFutureError" });
    } else if (form.dob && !isAtLeast18(form.dob)) {
      nextErrors.dob = intl.formatMessage({ id: "lender.profile.dobMinAgeError" });
    }
    if (form.fathersName && !NAME_REGEX.test(form.fathersName)) {
      nextErrors.fathersName = intl.formatMessage({ id: "lender.profile.fathersNameFormatError" });
    }
    if (form.addressPincode && !PINCODE_REGEX.test(form.addressPincode)) {
      nextErrors.addressPincode = intl.formatMessage({ id: "lender.profile.addressPincodeFormatError" });
    }

    if (hasBankInput) {
      if (!ACCOUNT_NUMBER_REGEX.test(form.accountNumber)) {
        nextErrors.accountNumber = intl.formatMessage({ id: "lender.profile.accountNumberFormatError" });
      }
      if (!IFSC_REGEX.test(form.ifsc)) {
        nextErrors.ifsc = intl.formatMessage({ id: "lender.profile.ifscFormatError" });
      }
      if (!form.accountHolderName.trim()) {
        nextErrors.accountHolderName = intl.formatMessage({ id: "lender.profile.accountHolderNameRequiredError" });
      }
      if (!form.bankName.trim()) {
        nextErrors.bankName = intl.formatMessage({ id: "lender.profile.bankNameRequiredError" });
      }
      if (!form.accountType) {
        nextErrors.accountType = intl.formatMessage({ id: "lender.profile.accountTypeRequiredError" });
      }
    }

    if (hasNomineeInput) {
      if (!form.nomineeName.trim()) {
        nextErrors.nomineeName = intl.formatMessage({ id: "lender.profile.nomineeNameRequiredError" });
      } else if (!NAME_REGEX.test(form.nomineeName)) {
        nextErrors.nomineeName = intl.formatMessage({ id: "lender.profile.fathersNameFormatError" });
      }
      if (!form.nomineeRelationship) {
        nextErrors.nomineeRelationship = intl.formatMessage({
          id: "lender.profile.nomineeRelationshipRequiredError",
        });
      }
      if (form.nomineeContact && !PHONE_REGEX.test(form.nomineeContact)) {
        nextErrors.nomineeContact = intl.formatMessage({ id: "lender.profile.nomineeContactFormatError" });
      }
    }

    if (form.employmentType && form.monthlyIncome && (Number.isNaN(Number(form.monthlyIncome)) || Number(form.monthlyIncome) < 0)) {
      nextErrors.monthlyIncome = intl.formatMessage({ id: "lender.profile.monthlyIncomeFormatError" });
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;

    try {
      await updateApplicantProfile({
        id: applicationId,
        payload: {
          ...(form.email && form.email !== data?.profile.email ? { email: form.email } : {}),
          ...(form.phone ? { phone: toIndianE164(form.phone) } : {}),
          ...(form.branchId && form.branchId !== data?.branchSelection.defaultBranch?.branchId
            ? { branchId: form.branchId }
            : {}),
          profile: {
            ...(form.pan ? { pan: form.pan } : {}),
            ...(form.aadhaar ? { aadhaar: form.aadhaar } : {}),
            ...(form.dob ? { dob: isoToWireDate(form.dob) } : {}),
            ...(form.fathersName.trim() ? { fathersName: form.fathersName.trim() } : {}),
            ...(form.gender ? { gender: form.gender } : {}),
            ...(form.addressLine1
              ? {
                  addresses: [
                    {
                      type: "CURRENT" as const,
                      line1: form.addressLine1,
                      line2: form.addressLine2 || undefined,
                      city: form.addressCity,
                      state: form.addressState,
                      pincode: form.addressPincode,
                    },
                  ],
                }
              : {}),
            ...(hasBankInput
              ? {
                  bankDetail: {
                    accountNumber: form.accountNumber,
                    ifsc: form.ifsc,
                    accountHolderName: form.accountHolderName.trim(),
                    bankName: form.bankName.trim(),
                    accountType: form.accountType as "SAVINGS" | "CURRENT",
                  },
                }
              : {}),
            ...(hasNomineeInput
              ? {
                  nominee: {
                    name: form.nomineeName.trim(),
                    relationship: form.nomineeRelationship as NomineeRelationship,
                    phone: form.nomineeContact ? toIndianE164(form.nomineeContact) : undefined,
                  },
                }
              : {}),
            ...(form.employmentType
              ? {
                  employmentDetail: {
                    employmentType: form.employmentType as EmploymentType,
                    employerName: form.employerName.trim() || undefined,
                    designation: form.designation.trim() || undefined,
                    monthlyIncome: form.monthlyIncome ? Number(form.monthlyIncome) : undefined,
                  },
                }
              : {}),
          },
        },
      }).unwrap();
      setIsEditing(false);
    } catch (submitError) {
      setFormError(
        isQueryApiError(submitError)
          ? submitError.message
          : intl.formatMessage({ id: "lender.onboarding.review.profileUpdateError" }),
      );
    }
  }

  if (error) {
    return (
      <AlertBanner status="error" className="mt-6">
        {isQueryApiError(error) ? error.message : intl.formatMessage({ id: "lender.onboarding.review.profileLoadError" })}
      </AlertBanner>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="mt-6 flex flex-col gap-4 rounded-md border border-border bg-surface-raised p-6">
        <Skeleton variant="text" className="h-5 w-40" />
        <Skeleton variant="rect" className="h-10 w-full" />
        <Skeleton variant="rect" className="h-10 w-full" />
      </div>
    );
  }

  const { profile } = data;
  const address = profile.addresses.find((a) => a.type === "CURRENT") ?? profile.addresses[0];
  const bank = profile.bankDetails[0];

  if (isEditing) {
    return (
      <form
        onSubmit={handleSubmit}
        noValidate
        className="mt-6 flex flex-col gap-6 rounded-md border border-border bg-surface-raised p-6"
      >
        {formError && <AlertBanner status="error">{formError}</AlertBanner>}

        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.onboarding.review.applicantProfileHeading" />
        </Heading>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField label={intl.formatMessage({ id: "lender.profile.emailLabel" })} error={errors.email}>
            <Input type="email" value={form.email} onChange={(event) => updateField("email", event.target.value)} />
          </FormField>
          <FormField
            label={intl.formatMessage({ id: "lender.onboarding.review.phoneLabel" })}
            error={errors.phone}
          >
            <div className="flex">
              <span
                className="flex items-center rounded-sm border border-border bg-surface px-3 text-sm text-text-muted"
                aria-hidden="true"
              >
                +91
              </span>
              <Input
                value={form.phone}
                onChange={(event) => updateField("phone", event.target.value.replace(/\D/g, "").slice(0, 10))}
                inputMode="numeric"
                className="flex-1"
              />
            </div>
          </FormField>
        </div>

        {data.branchSelection.branches.length > 0 && (
          <SelectField
            label={intl.formatMessage({ id: "lender.onboarding.branchLabel" })}
            placeholder={intl.formatMessage({ id: "lender.onboarding.branchPlaceholder" })}
            value={form.branchId}
            onValueChange={(value) => updateField("branchId", value)}
            options={data.branchSelection.branches.map((branch) => ({
              value: branch.branchId,
              label: `${branch.branchName} (${branch.readableId})`,
            }))}
          />
        )}

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField
            label={intl.formatMessage({ id: "lender.profile.panLabel" })}
            error={errors.pan}
            hint={errors.pan ? undefined : intl.formatMessage({ id: "lender.profile.panEditHint" })}
          >
            <Input
              value={form.pan}
              onChange={(event) => updateField("pan", event.target.value.toUpperCase())}
              placeholder={intl.formatMessage({ id: "lender.profile.panPlaceholder" })}
              maxLength={10}
            />
          </FormField>
          <FormField
            label={intl.formatMessage({ id: "lender.profile.aadhaarLabel" })}
            error={errors.aadhaar}
            hint={errors.aadhaar ? undefined : intl.formatMessage({ id: "lender.profile.aadhaarEditHint" })}
          >
            <Input
              value={form.aadhaar}
              onChange={(event) => updateField("aadhaar", event.target.value.replace(/\D/g, ""))}
              placeholder={intl.formatMessage({ id: "lender.profile.aadhaarPlaceholder" })}
              maxLength={12}
            />
          </FormField>
        </div>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField
            label={intl.formatMessage({ id: "lender.profile.fathersNameLabel" })}
            error={errors.fathersName}
          >
            <Input value={form.fathersName} onChange={(event) => updateField("fathersName", event.target.value)} />
          </FormField>
          <FormField label={intl.formatMessage({ id: "lender.profile.dateOfBirthLabel" })} error={errors.dob}>
            <Input type="date" value={form.dob} onChange={(event) => updateField("dob", event.target.value)} />
          </FormField>
        </div>

        <SelectField
          label={intl.formatMessage({ id: "lender.profile.genderLabel" })}
          placeholder={intl.formatMessage({ id: "lender.profile.genderPlaceholder" })}
          value={form.gender}
          onValueChange={(value) => updateField("gender", value)}
          options={[
            { value: "MALE", label: intl.formatMessage({ id: "lender.profile.genderMale" }) },
            { value: "FEMALE", label: intl.formatMessage({ id: "lender.profile.genderFemale" }) },
            { value: "OTHER", label: intl.formatMessage({ id: "lender.profile.genderOther" }) },
          ]}
        />

        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.profile.addressesHeading" />
        </Heading>

        <FormField label={intl.formatMessage({ id: "lender.profile.addressLine1Label" })}>
          <Input value={form.addressLine1} onChange={(event) => updateField("addressLine1", event.target.value)} />
        </FormField>
        <FormField label={intl.formatMessage({ id: "lender.profile.addressLine2Label" })}>
          <Input value={form.addressLine2} onChange={(event) => updateField("addressLine2", event.target.value)} />
        </FormField>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <FormField label={intl.formatMessage({ id: "lender.profile.addressCityLabel" })}>
            <Input value={form.addressCity} onChange={(event) => updateField("addressCity", event.target.value)} />
          </FormField>
          <FormField label={intl.formatMessage({ id: "lender.profile.addressStateLabel" })}>
            <Input value={form.addressState} onChange={(event) => updateField("addressState", event.target.value)} />
          </FormField>
          <FormField
            label={intl.formatMessage({ id: "lender.profile.addressPincodeLabel" })}
            error={errors.addressPincode}
          >
            <Input
              value={form.addressPincode}
              onChange={(event) => updateField("addressPincode", event.target.value.replace(/\D/g, "").slice(0, 6))}
              maxLength={6}
            />
          </FormField>
        </div>

        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.profile.bankAccountDetailsHeading" />
        </Heading>

        <FormField
          label={intl.formatMessage({ id: "lender.profile.accountNumberLabel" })}
          error={errors.accountNumber}
          required={hasBankInput}
          hint={errors.accountNumber ? undefined : intl.formatMessage({ id: "lender.profile.accountNumberEditHint" })}
        >
          <Input
            value={form.accountNumber}
            onChange={(event) => updateField("accountNumber", event.target.value.replace(/\D/g, ""))}
            maxLength={18}
          />
        </FormField>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField
            label={intl.formatMessage({ id: "lender.profile.ifscCodeLabel" })}
            error={errors.ifsc}
            required={hasBankInput}
          >
            <Input
              value={form.ifsc}
              onChange={(event) => updateField("ifsc", event.target.value.toUpperCase())}
              maxLength={11}
            />
          </FormField>
          <FormField
            label={intl.formatMessage({ id: "lender.profile.accountHolderNameLabel" })}
            error={errors.accountHolderName}
            required={hasBankInput}
          >
            <Input
              value={form.accountHolderName}
              onChange={(event) => updateField("accountHolderName", event.target.value)}
            />
          </FormField>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField
            label={intl.formatMessage({ id: "lender.profile.bankNameLabel" })}
            error={errors.bankName}
            required={hasBankInput}
          >
            <Input value={form.bankName} onChange={(event) => updateField("bankName", event.target.value)} />
          </FormField>
          <SelectField
            label={intl.formatMessage({ id: "lender.profile.accountTypeLabel" })}
            placeholder={intl.formatMessage({ id: "lender.profile.accountTypePlaceholder" })}
            value={form.accountType}
            onValueChange={(value) => updateField("accountType", value as FormState["accountType"])}
            error={errors.accountType}
            required={hasBankInput}
            options={[
              { value: "SAVINGS", label: intl.formatMessage({ id: "lender.profile.accountTypeSavings" }) },
              { value: "CURRENT", label: intl.formatMessage({ id: "lender.profile.accountTypeCurrent" }) },
            ]}
          />
        </div>

        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.profile.nomineeDetailsHeading" />
        </Heading>

        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField
            label={intl.formatMessage({ id: "lender.profile.nomineeNameLabel" })}
            error={errors.nomineeName}
            required={hasNomineeInput}
          >
            <Input value={form.nomineeName} onChange={(event) => updateField("nomineeName", event.target.value)} />
          </FormField>
          <FormField
            label={intl.formatMessage({ id: "lender.profile.nomineeContactNumberLabel" })}
            error={errors.nomineeContact}
          >
            <div className="flex">
              <span
                className="flex items-center rounded-sm border border-border bg-surface px-3 text-sm text-text-muted"
                aria-hidden="true"
              >
                +91
              </span>
              <Input
                value={form.nomineeContact}
                onChange={(event) => updateField("nomineeContact", event.target.value.replace(/\D/g, "").slice(0, 10))}
                inputMode="numeric"
                className="flex-1"
              />
            </div>
          </FormField>
        </div>
        <SelectField
          label={intl.formatMessage({ id: "lender.profile.nomineeRelationshipLabel" })}
          placeholder={intl.formatMessage({ id: "lender.profile.relationshipPlaceholder" })}
          value={form.nomineeRelationship}
          onValueChange={(value) => updateField("nomineeRelationship", value as NomineeRelationship)}
          error={errors.nomineeRelationship}
          required={hasNomineeInput}
          options={Object.entries(RELATIONSHIP_LABEL_IDS).map(([value, labelId]) => ({
            value,
            label: intl.formatMessage({ id: labelId }),
          }))}
        />

        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.profile.employmentDetailsHeading" />
        </Heading>

        <SelectField
          label={intl.formatMessage({ id: "lender.profile.employmentTypeLabel" })}
          placeholder={intl.formatMessage({ id: "lender.profile.employmentTypePlaceholder" })}
          value={form.employmentType}
          onValueChange={(value) => updateField("employmentType", value as EmploymentType)}
          options={EMPLOYMENT_TYPE_OPTIONS.map((type) => ({
            value: type,
            label: intl.formatMessage({ id: EMPLOYMENT_TYPE_LABEL_IDS[type] }),
          }))}
        />
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          <FormField label={intl.formatMessage({ id: "lender.profile.employerNameLabel" })}>
            <Input value={form.employerName} onChange={(event) => updateField("employerName", event.target.value)} />
          </FormField>
          <FormField label={intl.formatMessage({ id: "lender.profile.designationLabel" })}>
            <Input value={form.designation} onChange={(event) => updateField("designation", event.target.value)} />
          </FormField>
        </div>
        <FormField
          label={intl.formatMessage({ id: "lender.profile.monthlyIncomeLabel" })}
          error={errors.monthlyIncome}
        >
          <Input
            value={form.monthlyIncome}
            onChange={(event) => updateField("monthlyIncome", event.target.value.replace(/[^\d]/g, ""))}
            inputMode="numeric"
          />
        </FormField>

        <div className="flex gap-3">
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? (
              <FormattedMessage id="lender.onboarding.review.savingProfileButton" />
            ) : (
              <FormattedMessage id="lender.onboarding.review.saveProfileButton" />
            )}
          </Button>
          <Button type="button" variant="secondary" onClick={handleCancel} disabled={isSubmitting}>
            <FormattedMessage id="lender.onboarding.review.cancelProfileButton" />
          </Button>
        </div>
      </form>
    );
  }

  return (
    <div className="mt-6 rounded-md border border-border bg-surface-raised">
      <div className="flex items-center justify-between gap-3 px-6 py-4">
        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.onboarding.review.applicantProfileHeading" />
        </Heading>
        <Button type="button" variant="ghost" density="compact" onClick={handleStartEdit}>
          <FormattedMessage id="lender.onboarding.review.editProfileButton" />
        </Button>
      </div>

      <section className="px-6 py-3">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-3">
          <ProfileField labelId="lender.profile.emailLabel" value={profile.email} />
          <ProfileField labelId="lender.onboarding.review.phoneLabel" value={profile.phone} />
          <ProfileField labelId="lender.onboarding.branchLabel" value={data.branchSelection.defaultBranch?.branchName ?? "–"} />
          {/* loanAmount is BORROWER-only per lendcord-core's ApplicationJourney
              (LENDER/STAFF/AGENT applications carry no loan terms at all —
              see docs/flows/onboarding-review-flow.md) — hidden for the
              other three applicationTypes rather than shown empty. */}
          {data.application.applicationType === "BORROWER" && (
            <ProfileField
              labelId="lender.profile.loanAmountLabel"
              value={profile.loanAmount != null ? currencyFormatter.format(profile.loanAmount) : "–"}
              valueClassName={tabularNumsClass}
            />
          )}
          <ProfileField labelId="lender.profile.panLabel" value={profile.pan ?? "–"} />
          <ProfileField labelId="lender.profile.aadhaarLabel" value={profile.aadhaar ?? "–"} />
          <ProfileField
            labelId="lender.profile.dateOfBirthLabel"
            value={profile.dob ? formatWireDate(profile.dob) : "–"}
          />
          <ProfileField labelId="lender.profile.fathersNameLabel" value={profile.fathersName ?? "–"} />
          <ProfileField
            labelId="lender.profile.genderLabel"
            value={profile.gender ? intl.formatMessage({ id: GENDER_LABEL_IDS[profile.gender] ?? "lender.profile.genderOther" }) : "–"}
          />
        </div>
      </section>

      <section className="border-t border-border px-6 py-3">
        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.profile.addressesHeading" />
        </Heading>
        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
          <div className="sm:col-span-3">
            <ProfileField
              labelId="lender.profile.addressLine1Label"
              value={address ? `${address.line1}${address.line2 ? `, ${address.line2}` : ""}` : "–"}
            />
          </div>
          <ProfileField labelId="lender.profile.addressCityLabel" value={address?.city ?? "–"} />
          <ProfileField labelId="lender.profile.addressStateLabel" value={address?.state ?? "–"} />
          <ProfileField labelId="lender.profile.addressPincodeLabel" value={address?.pincode ?? "–"} />
        </div>
      </section>

      <section className="border-t border-border px-6 py-3">
        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.profile.bankAccountDetailsHeading" />
        </Heading>
        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
          <ProfileField labelId="lender.profile.bankNameLabel" value={bank?.bankName ?? "–"} />
          <ProfileField labelId="lender.profile.accountTypeLabel" value={bank?.accountType ?? "–"} />
          <ProfileField labelId="lender.profile.accountNumberLabel" value={bank?.accountNumber ?? "–"} />
          <ProfileField labelId="lender.profile.ifscCodeLabel" value={bank?.ifsc ?? "–"} />
          <ProfileField labelId="lender.profile.accountHolderNameLabel" value={bank?.accountHolderName ?? "–"} />
        </div>
      </section>

      <section className="border-t border-border px-6 py-3">
        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.profile.nomineeDetailsHeading" />
        </Heading>
        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
          <ProfileField labelId="lender.profile.nomineeNameLabel" value={profile.nominee?.name ?? "–"} />
          <ProfileField labelId="lender.profile.nomineeContactNumberLabel" value={profile.nominee?.phone ?? "–"} />
          <ProfileField
            labelId="lender.profile.nomineeRelationshipLabel"
            value={profile.nominee?.relationship ? intl.formatMessage({ id: RELATIONSHIP_LABEL_IDS[profile.nominee.relationship] }) : "–"}
          />
        </div>
      </section>

      <section className="border-t border-border px-6 py-3">
        <Heading level={4} className="text-accent-600">
          <FormattedMessage id="lender.profile.employmentDetailsHeading" />
        </Heading>
        <div className="mt-4 grid grid-cols-1 gap-6 sm:grid-cols-3">
          <ProfileField
            labelId="lender.profile.employmentTypeLabel"
            value={
              profile.employmentDetail?.employmentType
                ? intl.formatMessage({ id: EMPLOYMENT_TYPE_LABEL_IDS[profile.employmentDetail.employmentType] })
                : "–"
            }
          />
          <ProfileField labelId="lender.profile.employerNameLabel" value={profile.employmentDetail?.employerName ?? "–"} />
          <ProfileField labelId="lender.profile.designationLabel" value={profile.employmentDetail?.designation ?? "–"} />
        </div>
      </section>
    </div>
  );
}
